'use client';

import * as React from 'react';
import Graph from 'react-graph-vis';
import { renderToStaticMarkup } from 'react-dom/server';
import { Edge, Network, Node, Options } from 'vis';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Icon from '@/components/ui/Icon';
import LoadingContent from '@/components/ui/LoadingContent';
import { useOverlay } from '@/components/hooks/useOverlay';
import LayoutOverlay from '@/components/ui/LayoutOverlay';
import Block from '@/components/ui/Block';
import Overlay from '@/components/ui/Overlay';
import {
  InfopankRelationsGraphFilterDto,
  InfopankRelationsGraphLegendItemType,
  InfopankRelationsGraphLinkCategory,
} from '@/apiTypes/types';
import Callout from '@/components/ui/Callout';
import { DOUBLE_CLICK_DELAY } from '@/lib/constants';
import { useTimeout } from '@/components/hooks/useTimeout';
import './styles.scss';

export enum NodeType {
  Person = 'Person',
  Company = 'Company',
}

export enum LinkCategory {
  Owners = 'Owners',
  Ownerships = 'Ownerships',
  CouncilMembers = 'CouncilMembers',
  BoardMembers = 'BoardMembers',
  Beneficiaries = 'Beneficiaries',
  BusinessRegisterBeneficiaries = 'BusinessRegisterBeneficiaries',
  Employees = 'Employees',
  History = 'History',
  Others = 'Others',
  Active = 'Active',
}

export const graphLinkToLinkCategoryMap: Record<InfopankRelationsGraphLinkCategory, LinkCategory> = {
  ['Owners']: LinkCategory.Owners,
  ['BoardMembers']: LinkCategory.BoardMembers,
  ['CouncilMembers']: LinkCategory.CouncilMembers,
  ['Ownerships']: LinkCategory.Ownerships,
  ['AriregisterBeneficiaries']: LinkCategory.BusinessRegisterBeneficiaries,
  ['Employees']: LinkCategory.Employees,
  ['InfopankBeneficiaries']: LinkCategory.Beneficiaries,
  ['Others']: LinkCategory.Others,
};

export const legendItemTypeToLinkCategoryMap: Record<InfopankRelationsGraphLegendItemType, LinkCategory> = {
  ['Owners']: LinkCategory.Owners,
  ['BoardMembers']: LinkCategory.BoardMembers,
  ['CouncilMembers']: LinkCategory.CouncilMembers,
  ['Ownerships']: LinkCategory.Ownerships,
  ['InfopankBeneficiaries']: LinkCategory.Beneficiaries,
  ['AriregisterBeneficiaries']: LinkCategory.BusinessRegisterBeneficiaries,
  ['Employees']: LinkCategory.Employees,
  ['History']: LinkCategory.History,
  ['Others']: LinkCategory.Others,
  ['Active']: LinkCategory.Active,
};

export enum NodeWarningType {
  Good = 'Good',
  Cautionary = 'Cautionary',
  Severe = 'Severe',
  Missing = 'Missing',
  None = 'None',
}

export type DataNode = {
  name: string;
  root: boolean;
  warningType: keyof typeof NodeWarningType;
  url?: string;
  id: number | string;
  type: keyof typeof NodeType;
  uniqueId: string;
  level?: number;
  dataUrl?: string | null;
};

export type DataLink = {
  source: string | number;
  target: string | number;
  active: boolean;
  label?: string;
  category: keyof typeof LinkCategory;
};

export const getLinkColor = (category: keyof typeof LinkCategory) => {
  switch (category) {
    case LinkCategory.Owners:
      return '#EBF73B';
    case LinkCategory.Ownerships:
      return '#F7AA3B';
    case LinkCategory.CouncilMembers:
      return '#594139';
    case LinkCategory.BoardMembers:
      return '#0856C3';
    case LinkCategory.Beneficiaries:
      return '#08C414';
    case LinkCategory.BusinessRegisterBeneficiaries:
      return '#F73C3B';
    case LinkCategory.Employees:
      return '#021631';
    case LinkCategory.Others:
      return '#740673';
    case LinkCategory.Active:
      return 'linear-gradient(90deg, #ebf73b 10%, #0856c3 10%, #0856c3 20%, #08c414 20%, #08c414 30%, #f7aa3b 30%, #f7aa3b 40%, #740673 40%, #740673 50%, #ebf73b 50%, #ebf73b 60%, #0856c3 60%, #0856c3 70%, #08c414 70%, #08c414 80%, #f7aa3b 80%, #f7aa3b 90%, #740673 90%, #740673 100%)';
    case LinkCategory.History:
      return 'linear-gradient(90deg, #ebf73b 6.52%, #ffffff 6.52%, #ffffff 10.87%, #0856c3 10.87%, #0856c3 17.39%, #fff 17.39%, #fff 21.74%, #08c414 21.74%, #08c414 28.26%, #fff 28.26%, #fff 32.61%, #f7aa3b 32.61%, #f7aa3b 39.13%, #fff 39.13%, #fff 43.48%, #740673 43.48%, #740673 50%, #ebf73b 50%, #ebf73b 56.52%, #ffffff 56.52%, #ffffff 60.87%, #0856c3 60.87%, #0856c3 67.39%, #fff 67.39%, #fff 71.74%, #08c414 71.74%, #08c414 78.26%, #fff 78.26%, #fff 82.61%, #f7aa3b 82.61%, #f7aa3b 89.13%, #fff 89.13%, #fff 93.48%, #740673 93.48%, #740673 100%)';
    default:
      return 'black';
  }
};

function getWarningIndicatorColor(warning: keyof typeof NodeWarningType) {
  switch (warning) {
    case NodeWarningType.Good:
      return '#08C414';
    case NodeWarningType.Cautionary:
      return '#FBE429';
    case NodeWarningType.Severe:
      return '#C41408';
    case NodeWarningType.Missing:
      return '#C9CBCF';
    default:
      return 'transparent';
  }
}

const CustomMarker: React.FunctionComponent<{
  type: keyof typeof NodeType;
  title: string;
  isRoot: boolean;
  warning: keyof typeof NodeWarningType;
  hasHref: boolean;
}> = (props) => {
  const w = 200;
  const h = 96;
  const hasBubble = props.warning !== 'None';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={props.isRoot ? w * 1.5 : w}
      height={props.isRoot ? h * 1.5 : h}
      viewBox={`0 0 ${w} ${h}`}
    >
      <foreignObject
        x="0"
        y="0"
        width="100%"
        height="100%"
      >
        <div
          style={{
            width: w,
            height: h,
            boxSizing: 'border-box',
            display: 'flex',
            padding: 20,
            gap: 0,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontFamily: 'sans-serif',
            overflow: 'hidden',
          }}
          // @ts-expect-error type
          xmlns="http://www.w3.org/1999/xhtml"
        >
          <div
            style={{
              flex: '0 0 48px',
              width: 48,
              height: 48,
              background: '#CEE1FD',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon
              width={28}
              height={28}
              kind={props.type === NodeType.Person ? 'person' : 'company'}
            />
          </div>
          <div
            style={{
              flex: '0 0 auto',
              overflow: 'hidden',
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              textAlign: 'center',
              color: props.hasHref ? '#0B6CF4' : '#021631',
              fontSize: 12,
              lineHeight: '16px',
              border: '1px solid #E4E5E7',
              backgroundColor: '#fff',
              // boxShadow: '0px 4px 10px 0px #0B6CF41F',
              padding: '4px 12px',
              borderRadius: 12,
              marginTop: 4,
            }}
          >
            {hasBubble && (
              <div
                style={{
                  flex: '0 0 16px',
                  marginLeft: -8,
                  width: 16,
                  height: 16,
                  backgroundColor: getWarningIndicatorColor(props.warning),
                  borderRadius: 8,
                }}
              />
            )}
            <div
              style={{
                flex: '0 1 auto',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {props.title}
            </div>
            {props.hasHref && (
              <Icon
                style={{
                  flex: '0 0 auto',
                }}
                width={16}
                height={16}
                kind="arrow02-right"
              />
            )}
          </div>
        </div>
      </foreignObject>
    </svg>
  );
};

export interface IChartRelationsProps {
  layout: 'relations' | 'ownership';
  nodes: DataNode[];
  links: DataLink[];
  addOn?: React.ReactNode;
  loading?: boolean;
  overlayId: string;
  pre?: React.ReactNode;
  post?: React.ReactNode;
  height?: string;
  selectedNode: DataNode | null;
  setSelectedNode: (val: DataNode | null) => void;
  filter?: InfopankRelationsGraphFilterDto;
}

const setNodes = (nodes: DataNode[], links: DataLink[]) => {
  return (nodes || []).map((node) => {
    const image =
      'data:image/svg+xml;charset=utf-8,' +
      encodeURIComponent(
        renderToStaticMarkup(
          <CustomMarker
            type={node.type}
            title={node.name}
            isRoot={node.root}
            warning={node.warningType}
            hasHref={!!node.url && !node.root}
          />,
        ),
      );

    const ch = links
      .map((link) => {
        return link.source.toString().split(';')[0] === node.id.toString();
      })
      .filter(Boolean).length;

    return {
      objectId: node.id,
      type: node.type,
      id: node.uniqueId,
      level: node.level || 1,
      url: node.url,
      size: node.root ? 120 : 60,
      shape: 'image',
      // borderWidth: 1,
      color: {
        background: 'rgba(0, 0, 0, 0)',
        highlight: {
          background: 'rgba(0, 0, 0, 0)',
        },
        hover: {
          background: 'rgba(0, 0, 0, 0)',
        },
      },
      shapeProperties: {
        useImageSize: true,
        useBorderWithImage: true,
        interpolation: true,
      },
      fixed: node.root,
      mass: node.root ? Math.max(ch, 1) : 1,
      image,
    };
  });
};

const setLinks = (links: DataLink[], layout: 'relations' | 'ownership') => {
  return (links || []).map((l) => {
    const isOwner = l.category === LinkCategory.Owners;

    return {
      from: l.source,
      to: l.target,
      ...(layout === 'ownership'
        ? {
            color: l.category === LinkCategory.Owners || l.category === LinkCategory.Ownerships ? '#93979F' : getLinkColor(l.category),
          }
        : {
            color: getLinkColor(l.category),
            smooth: {
              enabled: true,
              type: 'dynamic',
              roundness: 0.5,
            },
          }),
      dashes: !l.active,
      arrows: {
        to: {
          enabled: !isOwner,
          type: 'arrow',
        },
        from: {
          enabled: isOwner,
          type: 'arrow',
        },
      },
    };
  });
};

const getOptions = (layout: string, height: string | undefined, nodesLength: number) => ({
  autoResize: true,
  clickToUse: false,
  nodes: {
    borderWidth: 0,
    borderWidthSelected: 0,
  },
  interaction: {
    dragNodes: true,
    hover: false,
    hoverConnectedEdges: false,
    navigationButtons: false,
    keyboard: false,
    zoomView: true,
  },
  edges: {
    smooth: {
      enabled: true,
      type: 'cubicBezier',
      forceDirection: layout === 'ownership' ? 'vertical' : 'none',
      roundness: 0.5,
    },
    hoverWidth: 2,
    endPointOffset: {
      from: 10,
      to: 10,
    },
    width: 2,
    scaling: {
      min: 1,
      max: 1,
    },
    selectionWidth: 3,
    selfReference: {
      size: 200,
      renderBehindTheNode: true,
    },
  },

  physics: {
    enabled: layout !== 'ownership',
    barnesHut: {
      gravitationalConstant: -10000,
      centralGravity: 0.2,
      springLength: Math.max(80 + nodesLength * 2, 100),
      springConstant: 0.1,
      damping: 0.2,
      // avoidOverlap: 0.8,
    },
    // maxVelocity: 20,
    // minVelocity: 0.1,
    solver: 'barnesHut',
    // timestep: 0.35,
    stabilization: {
      enabled: true,
      iterations: 1000,
      fit: true,
    },
  },
  ...(layout === 'ownership'
    ? {
        layout: {
          hierarchical: {
            nodeSpacing: 200,
          },
        },
      }
    : {}),
  height: height || '500px',
  width: '977px',
});

const RenderChart: React.FunctionComponent<IChartRelationsProps> = (props) => {
  const { openOverlay } = useOverlay(props.overlayId);
  const { t } = useTranslation();

  const [timerCooldown, setTimerCooldown] = React.useState<number | null>(null);
  const [interactionLocked, setInteractionLocked] = React.useState(false);
  const [isTooMany, setIsTooMany] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [timerFit, setTimerFit] = React.useState<number | null>(null);
  const [shouldFit, setShouldFit] = React.useState(true);
  const [fitted, setFitted] = React.useState(false);
  const [net, setNet] = React.useState<Network | null>(null);
  const [graph, setGraph] = React.useState<{ nodes: Node[]; edges: Edge[] }>({
    nodes: setNodes(props.nodes, props.links),
    edges: setLinks(props.links, props.layout),
  });

  const nodesRef = React.useRef(props.nodes);
  const lastClickTime = React.useRef(0);
  const singleClickTimeout = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    nodesRef.current = props.nodes;
    setShouldFit(true);
  }, [props.nodes]);

  React.useEffect(() => {
    if (!props.loading) {
      setShouldFit(true);
    }
  }, [props.nodes]);

  React.useEffect(() => {
    net?.releaseNode();
    const root = graph.nodes[0];
    if (root && net) {
      net?.focus(root.id!, {
        locked: true,
        animation: {
          duration: 250,
          easingFunction: 'easeInOutQuad',
        },
      });
    }
  }, [props.filter]);

  const handleScroll = () => {
    if (!interactionLocked) {
      setInteractionLocked(true);
    }
    setTimerCooldown(null);
    setTimerCooldown(500);
  };

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    setMounted(true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      setMounted(false);
      setTimerCooldown(null);
      setInteractionLocked(false);
    };
  }, []);

  useTimeout(() => {
    setTimerCooldown(null);
    setInteractionLocked(false);
  }, timerCooldown);

  useTimeout(
    () => {
      if (shouldFit && !fitted) {
        setFitted(true);
        setShouldFit(false);
        net?.fit();
      }
    },
    timerFit,
    [shouldFit, fitted, setFitted, setShouldFit],
  );

  React.useEffect(() => {
    if (props.links.length > 100) {
      setIsTooMany(true);
    } else {
      setIsTooMany(false);
      setGraph({
        nodes: setNodes(props.nodes, props.links),
        edges: setLinks(props.links, props.layout),
      });
      setShouldFit(true);
    }
  }, [props]);

  const options: Options = getOptions(props.layout, props.height, graph.nodes.length);

  const events = {
    select: function () {
      // console.log(event);
    },
    stabilized: () => {
      setTimerFit(150);
    },
  };

  const getSelectedNode = (network: Network) => {
    const selectedNode = network.getSelectedNodes()[0];
    const selectedNodeId = selectedNode ? selectedNode.toString() : null;
    return nodesRef.current.find((n) => n.uniqueId === selectedNodeId) as (Node & { url: string }) | undefined;
  };

  const getNetwork = (network: Network) => {
    setNet(network);
  };

  React.useEffect(() => {
    if (net) {
      const handleClick = () => {
        const currentTime = Date.now();
        const timeSinceLastClick = currentTime - lastClickTime.current;
        if (timeSinceLastClick < DOUBLE_CLICK_DELAY) {
          return;
        }

        if (singleClickTimeout.current) {
          clearTimeout(singleClickTimeout.current);
        }

        singleClickTimeout.current = setTimeout(() => {
          const node = getSelectedNode(net) as DataNode;
          if (node) {
            props.setSelectedNode(node);
            if (node.uniqueId) {
              net.focus(node.uniqueId, {
                locked: true,
                animation: {
                  duration: 250,
                  easingFunction: 'easeInOutQuad',
                },
              });
            }
          }
        }, DOUBLE_CLICK_DELAY);
        lastClickTime.current = currentTime;
      };

      const handleDoubleClick = () => {
        if (singleClickTimeout.current) {
          clearTimeout(singleClickTimeout.current);
        }
        const node = getSelectedNode(net);
        if (node && node.url) {
          window.open(node.url, '_blank', 'noopener,noreferrer');
        }
      };

      net.on('click', handleClick);
      net.on('doubleClick', handleDoubleClick);

      return () => {
        net.off('click', handleClick);
        net.off('doubleClick', handleDoubleClick);
      };
    }
  }, [net, props]);

  return (
    <React.Fragment>
      {props.pre && <div className={'chart-relations__pre'}>{props.pre}</div>}

      <div className={'chart-relations'}>
        {isTooMany && (
          <div className={'chart-relations__add-on'}>
            <Callout
              intent="default"
              text={t('company.chart.connectionsLimit')}
              icon={'danger'}
            />
          </div>
        )}
        {props.loading && (
          <LoadingContent
            layout="absolute"
            size="small"
          />
        )}
        <div
          className={classNames('chart-relations__locked', { ['active']: interactionLocked })}
          onClick={() => {
            setTimerCooldown(null);
            setInteractionLocked(false);
          }}
        />
        {mounted && (
          <React.Fragment>
            <Graph
              graph={graph}
              options={options}
              events={events}
              getNetwork={getNetwork}
            />
            <div className={'chart-relations__tools'}>
              <button
                onClick={() => {
                  if (net) {
                    const scale = net.getScale();
                    net.moveTo({
                      scale: scale + 0.25,
                      animation: true,
                    });
                  }
                }}
              >
                <Icon
                  width={16}
                  height={16}
                  kind="plus"
                />
              </button>
              <button
                onClick={() => {
                  if (net) {
                    const scale = net.getScale();
                    net.moveTo({
                      scale: scale - 0.25 >= 0 ? scale - 0.25 : scale,
                      animation: true,
                    });
                  }
                }}
              >
                <Icon
                  width={16}
                  height={16}
                  kind="minus"
                />
              </button>
              <button
                onClick={() => {
                  openOverlay();
                }}
                className="mobile-hide"
              >
                <Icon
                  width={16}
                  height={16}
                  kind="fullscreen"
                />
              </button>
            </div>
          </React.Fragment>
        )}
      </div>

      {props.post && <div className={'chart-relations__post'}>{props.post}</div>}
    </React.Fragment>
  );
};

const ChartRelations: React.FunctionComponent<IChartRelationsProps> = (props) => {
  const { isOverlayOpen, zIndexOfOverlay, closeOverlay } = useOverlay(props.overlayId);
  return (
    <React.Fragment>
      <RenderChart {...props} />
      <Overlay
        isOpen={isOverlayOpen}
        onClose={closeOverlay}
        layout="default"
        zIndex={zIndexOfOverlay}
        hideClose={true}
      >
        <LayoutOverlay
          size="max"
          title="Seotud isikud ja ettevõtted"
          onClose={closeOverlay}
        >
          <Block
            width="full"
            theme="lightest"
          >
            <RenderChart
              {...props}
              height="100%"
            />
          </Block>
        </LayoutOverlay>
      </Overlay>
    </React.Fragment>
  );
};

ChartRelations.displayName = 'ChartRelations';

export default ChartRelations;
